/*
 * air-compression.js — native behaviour for the converted pulsio-compression PDP.
 * Replaces the Replo runtime for the interactions this page uses:
 *   1. Collapsibles / accordions (Delivery & Warranty, Size chart, Specs, FAQ)
 *      — Replo's runtime is gone, so without this every accordion renders open.
 *   2. Carousels (controller visuals, review strips) — dot + prev/next nav.
 *   3. Product-card click-nav to /products/<handle>.
 *   4. Variant (size) option-select -> keeps the hidden cart form in sync.
 *   5. Add-to-cart buttons (data-replo-add-product-variant-to-cart) -> AJAX
 *      /cart/add.js with bundled sections, then Impact theme variant:add /
 *      cart:change events to open the cart drawer (no /cart redirect).
 *   6. Sleek-style scroll reveals (CSS transitions + IntersectionObserver),
 *      including resuming Replo alchemy-fade elements left paused at opacity:0.
 * No external dependencies. Idempotent per element and safe to load once.
 */
(function () {
  if (window.__airCompressionInit) return;
  window.__airCompressionInit = true;

  var REVEAL_OPTS = { rootMargin: '0px 0px -8% 0px', threshold: 0.12 };
  var STAGGER_MS = 70;
  var CAROUSEL_TRANSITION = 'transform 450ms cubic-bezier(0.22, 0.61, 0.36, 1)';
  var reducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  function scopes() {
    return document.querySelectorAll('.air-compression-section, [id^="replo-section-"]');
  }

  var VARIANTS = (function () {
    try {
      var el = document.getElementById('air-variant-data');
      return el ? JSON.parse(el.textContent) : [];
    } catch (e) {
      return [];
    }
  })();

  function matchVariant(values) {
    for (var i = 0; i < VARIANTS.length; i++) {
      var v = VARIANTS[i];
      var ok = true;
      for (var p = 0; p < values.length; p++) {
        var want = values[p];
        if (want == null) continue;
        var have = v['o' + (p + 1)];
        if (String(have || '').trim().toLowerCase() !== String(want).trim().toLowerCase()) {
          ok = false;
          break;
        }
      }
      if (ok) return v;
    }
    return null;
  }

  function containerOf(el) {
    return el.closest('[data-replo-product-container]') || document;
  }
  function formOf(scope) {
    return (scope.querySelector ? scope : document).querySelector(
      'form[action*="/cart/add"], form[data-type="add-to-cart-form"]'
    );
  }

  function initOptionSelect(listbox) {
    if (listbox.__airOpt) return;
    listbox.__airOpt = true;
    var options = Array.prototype.slice.call(listbox.querySelectorAll('[role="option"]'));
    if (!options.length) return;
    var container = containerOf(listbox);
    // Replo hardcodes the selected-state styles (e.g. the blue ring class
    // r-ibamqh) onto the default option only and relied on its runtime to move
    // them. Diff the initially-selected option against the others so we toggle
    // *every* selected-only class — locale-independent, so the blue indicator
    // tracks the clicked size on EN/ES/DE alike (not just r-9ai6a9).
    var initSel =
      options.filter(function (o) {
        return o.getAttribute('aria-selected') === 'true';
      })[0] || options[0];
    var selectedClasses = [];
    (initSel.className || '').split(/\s+/).forEach(function (c) {
      if (!c) return;
      var onAll = options.every(function (o) {
        return o.classList.contains(c);
      });
      if (!onAll && selectedClasses.indexOf(c) === -1) selectedClasses.push(c);
    });
    if (selectedClasses.indexOf('r-9ai6a9') === -1) selectedClasses.push('r-9ai6a9');
    // Replo bakes the selected value into a separate "LABEL: VALUE" text element
    // (e.g. the "SIZE:"/"COLOUR:" prefix lives in one node, the value —
    // {{ reploSelectedVariant.optionN }} — in a sibling). That value node is
    // static after render, so the label never tracks client-side selection.
    // Find it by matching the initially-selected option's own (localized) text
    // against the text nodes outside every listbox, so this stays locale- and
    // option-name-independent (works for SIZE, COLOUR, etc.).
    var initLabel = (initSel.textContent || '').trim();
    var valueEl = null;
    if (initLabel && container && container.querySelectorAll) {
      var candidates = container.querySelectorAll('.alchemy-rte');
      for (var ci = 0; ci < candidates.length; ci++) {
        var cand = candidates[ci];
        if (cand.closest('[role="listbox"]')) continue;
        if ((cand.textContent || '').trim() === initLabel) {
          valueEl = cand;
          break;
        }
      }
    }
    function setValueLabel(text) {
      if (!valueEl) return;
      var leaf = valueEl;
      while (leaf.children && leaf.children.length === 1) leaf = leaf.children[0];
      leaf.textContent = text;
    }
    function select(opt) {
      options.forEach(function (o) {
        var on = o === opt;
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        selectedClasses.forEach(function (c) {
          o.classList.toggle(c, on);
        });
      });
      var label = (opt.textContent || '').trim();
      setValueLabel(label);
      var v = matchVariant([label]);
      if (v) {
        var form = formOf(container);
        if (form) {
          var idInput = form.querySelector('input[name="id"]');
          if (idInput) idInput.value = v.id;
        }
        if (window.AirSoldOut) window.AirSoldOut.syncContainer(container);
      }
    }
    options.forEach(function (opt) {
      var handler = function (e) {
        if (e) e.preventDefault();
        select(opt);
      };
      opt.addEventListener('click', handler);
      opt.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') handler(e);
      });
      if (opt.getAttribute('aria-selected') === 'true') select(opt);
    });
  }

  /* ---------------------------------------------------------------- *
   * 1. Collapsibles / accordions (aria-controls + hidden toggle)     *
   * ---------------------------------------------------------------- */
  function initCollapsible(root) {
    if (root.__airCollapsible) return;
    root.__airCollapsible = true;
    var trigger = root.querySelector('[aria-controls]');
    if (!trigger) return;
    var contentId = trigger.getAttribute('aria-controls');
    var content = contentId ? document.getElementById(contentId) : null;
    function set(open) {
      root.setAttribute('data-state', open ? 'open' : 'closed');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (content) {
        if (open) content.removeAttribute('hidden');
        else content.setAttribute('hidden', '');
        content.setAttribute('data-state', open ? 'open' : 'closed');
      }
    }
    var toggle = function (e) {
      if (e) e.preventDefault();
      set(root.getAttribute('data-state') !== 'open');
    };
    trigger.addEventListener('click', toggle);
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') toggle(e);
    });
  }

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function dispatchCartAdded(responseJson, cartContent) {
    cartContent.sections = responseJson.sections;
    var items = responseJson.items || [responseJson];
    document.documentElement.dispatchEvent(
      new CustomEvent('variant:add', { bubbles: true, detail: { items: items, cart: cartContent } })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent },
      })
    );
    openCartFallback();
  }

  function openCartFallback() {
    /* This store's cart drawer is UpCart (AfterSell), not Shopify's native
     * cart-drawer. UpCart exposes upcartRegisterAddToCart() which refreshes its
     * contents and auto-opens the drawer after a headless /cart/add.js. */
    if (typeof window.upcartRegisterAddToCart === 'function') {
      try {
        window.upcartRegisterAddToCart();
        return;
      } catch (e) {}
    }
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      var cartType =
        window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.cartType;
      if (cartType !== 'drawer') {
        try {
          drawer.show();
        } catch (e) {}
      }
      return;
    }
    var legacy = document.querySelector('#CartDrawer, .js-drawer--cart');
    if (legacy) {
      var opener = document.querySelector('[aria-controls="cart-drawer"]');
      if (opener) {
        opener.click();
        return;
      }
    }
    window.location.href =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_url) || '/cart';
  }

  function submitFormFallback(form) {
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  function addVariantToCart(body, busyEl, form) {
    if (busyEl) busyEl.setAttribute('aria-busy', 'true');
    var sectionsToBundle = ['variant-added'];
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );
    } catch (e) {}
    var payload = {
      id: body.id,
      quantity: body.quantity || 1,
      sections: sectionsToBundle.join(','),
      sections_url: routeRoot() + 'variants/' + body.id,
    };
    if (body.selling_plan) payload.selling_plan = body.selling_plan;
    return fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        return r.json().then(function (d) {
          return { ok: r.ok, d: d };
        });
      })
      .then(function (res) {
        if (busyEl) busyEl.removeAttribute('aria-busy');
        if (!res.ok) {
          submitFormFallback(form);
          return;
        }
        return fetch(routeRoot() + 'cart.js')
          .then(function (r) {
            return r.json();
          })
          .then(function (cartContent) {
            dispatchCartAdded(res.d, cartContent);
          });
      })
      .catch(function () {
        if (busyEl) busyEl.removeAttribute('aria-busy');
        submitFormFallback(form);
      });
  }

  function initAddToCart(btn) {
    if (btn.__airAtc) return;
    btn.__airAtc = true;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.getAttribute('aria-disabled') === 'true') return;
      var container = containerOf(btn);
      var form = formOf(container);
      if (!form) return;
      var idInput = form.querySelector('input[name="id"]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      var body = {
        id: idInput && idInput.value,
        quantity: (qtyInput && parseInt(qtyInput.value, 10)) || 1,
      };
      var sp = form.querySelector('input[name="selling_plan"]');
      if (sp && sp.value) body.selling_plan = sp.value;
      if (!body.id) {
        submitFormFallback(form);
        return;
      }
      addVariantToCart(body, btn, form);
    });
  }

  /* ---------------------------------------------------------------- *
   * 2. Carousels (transform-based, prev/next + dots + drag)          *
   * ---------------------------------------------------------------- */
  function initCarousel(root) {
    if (window.AirCarousel && window.AirCarousel.init) {
      return window.AirCarousel.init(root);
    }
  }

  /* ---------------------------------------------------------------- *
   * 3. Product-card click nav                                        *
   * ---------------------------------------------------------------- */
  function initProductNav(root) {
    var cards = root.querySelectorAll('[data-replo-product-handle]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.__airNav) return;
      // Never hijack a real buy box / add-to-cart card. The free-gift card
      // (e.g. the "FREE Pulsio AIR" block) carries a vestigial hidden
      // <product-form> but no add-to-cart button, so key the skip on the ATC
      // button — that leaves the gift card navigable.
      if (card.querySelector('[data-replo-add-product-variant-to-cart]')) return;
      var handle = (card.getAttribute('data-replo-product-handle') || '').trim();
      if (!handle || handle === 'empty-product') return;
      card.__airNav = true;
      card.style.cursor = 'pointer';
      card.addEventListener('click', function (e) {
        // Ignore clicks on genuinely interactive descendants (links, buttons,
        // carousel controls, option chips). The gift card is itself
        // role="button", so exempt the card element itself from the guard.
        var interactive = e.target.closest(
          'a[href], button, form, input, select, [role="button"], [role="option"], [data-replo-part="nav-button"], [aria-label^="Go to slide"], [data-replo-add-product-variant-to-cart]'
        );
        if (interactive && interactive !== card) return;
        // Locale-aware product URL — Shopify.routes.root carries the /es/, /de/…
        // prefix, so the gift navigates to the localized product page.
        window.location.href = routeRoot() + 'products/' + handle;
      });
    });
  }

  /* ---------------------------------------------------------------- *
   * 3b. "How Do We Compare?" buy-box button -> comparison modal      *
   * The converted PDP renders the comparison table inline (there is  *
   * no modal in the conversion) and left this buy-box button inert.  *
   * Open a real overlay modal containing a clone of the inline        *
   * comparison section so the button gets a proper popup. Bound by    *
   * stable data-rid so it works on every locale (label is translated).*
   * The inline comparison section is left in place on the page (it is *
   * a legitimate template section, s3); the modal clones only the dark   *
   * "Why Choose Us" header + 3-column comparison table (not the full s3  *
   * section or hero imagery above the table).                           *
   * ---------------------------------------------------------------- */
  var compareModal = null;
  var compareLastFocus = null;

  function closeCompareModal() {
    if (!compareModal || compareModal.getAttribute('hidden') !== null) return;
    compareModal.classList.remove('is-open');
    document.documentElement.classList.remove('air-compare-modal-open');
    document.body.classList.remove('air-compare-modal-open');
    var overlay = compareModal;
    window.setTimeout(function () {
      overlay.setAttribute('hidden', '');
    }, 220);
    if (compareLastFocus && typeof compareLastFocus.focus === 'function') {
      try {
        compareLastFocus.focus();
      } catch (e) {}
    }
  }

  function openCompareModal() {
    if (!compareModal) return;
    compareLastFocus = document.activeElement;
    compareModal.removeAttribute('hidden');
    // force reflow so the transition runs from the hidden state
    void compareModal.offsetWidth;
    compareModal.classList.add('is-open');
    document.documentElement.classList.add('air-compare-modal-open');
    document.body.classList.add('air-compare-modal-open');
    var closeBtn = compareModal.querySelector('.air-compare-modal__close');
    if (closeBtn) {
      try {
        closeBtn.focus();
      } catch (e) {}
    }
  }

  function prepareCompareClone(source) {
    var clone = source.cloneNode(true);
    // Display-only clone: strip ids (avoid duplicates with the live inline
    // section, e.g. the hidden price <form>) and clear any paused reveal state
    // so the comparison is fully visible inside the modal.
    if (clone.removeAttribute) clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach(function (n) {
      n.removeAttribute('id');
    });
    clone.querySelectorAll('.air-reveal-pending').forEach(function (n) {
      n.classList.remove('air-reveal-pending');
      n.classList.add('air-reveal-visible');
    });
    return clone;
  }

  function findCompareModalSources() {
    var marked = document.querySelectorAll('[data-air-compare-modal-source]');
    if (marked.length) {
      return Array.prototype.slice.call(marked);
    }

    var section = document.querySelector(
      '[data-rid="506b0a42-5dbc-4456-a82d-6088729213b1"]'
    );
    if (section) {
      var nodes = [
        section.querySelector('[data-rid="22fbb4b6-f596-4113-88ef-2206fe52e0d1"]'),
        section.querySelector('[data-rid="60c6d815-a43f-47fc-bbd0-4b0685320cd8"]'),
        section.querySelector('[data-rid="658e9dd4-7229-4672-b62e-4d0c78658990"]'),
      ].filter(Boolean);
      if (nodes.length) return nodes;
    }

    return null;
  }

  function buildCompareModal(sources) {
    var overlay = document.createElement('div');
    overlay.className = 'air-compare-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('hidden', '');

    var dialog = document.createElement('div');
    dialog.className = 'air-compare-modal__dialog air-compare-modal__dialog--table';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'air-compare-modal__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    var content = document.createElement('div');
    content.className = 'air-compare-modal__content air-compare-modal__content--table';

    var wrap = document.createElement('div');
    wrap.className = 'air-compare-modal__table-wrap r-1v6qolq';

    var list = Array.isArray(sources) ? sources : [sources];
    list.forEach(function (source) {
      wrap.appendChild(prepareCompareClone(source));
    });
    content.appendChild(wrap);

    dialog.appendChild(closeBtn);
    dialog.appendChild(content);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      closeCompareModal();
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeCompareModal();
    });

    return overlay;
  }

  function initCompare() {
    try {
      var trigger = document.querySelector(
        '[data-rid="08206f9f-8343-4b49-8181-b1a48aff61e6"]'
      );
      if (!trigger || trigger.__airCompare) return;
      // Wire the trigger as soon as it exists — do NOT gate on the modal
      // source being present right now. The comparison source lives in a
      // *different* section (s3) than this buy-box button (s1), so it may not
      // be resolvable the instant this runs (theme-editor re-render, section
      // load ordering, or a stale snippet). Resolving lazily at click time
      // means the button is never left permanently inert.
      trigger.__airCompare = true;
      trigger.style.cursor = 'pointer';
      var open = function (e) {
        if (e) e.preventDefault();
        if (!compareModal) {
          var sources = findCompareModalSources();
          if (!sources || !sources.length) return;
          compareModal = buildCompareModal(sources);
        }
        openCompareModal();
      };
      trigger.addEventListener('click', open);
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') open(e);
      });
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- *
   * 3c. Buy-box "Size Chart" link -> size chart modal                *
   * Replo opened a popup built from section settings at runtime; the *
   * conversion only kept the trigger (blue dot + underlined label).  *
   * Clone the hidden air-cs1-size-chart-source markup into the same  *
   * overlay pattern as initCompare. Bound by stable data-rid /       *
   * data-air-size-chart-source — locale-independent.                 *
   * ---------------------------------------------------------------- */
  var sizeChartModal = null;
  var sizeChartLastFocus = null;

  var SIZE_CHART_TRIGGER_RIDS = [
    '55d57b1a-fed6-4ade-ae6f-adf535632843',
    '54cf4689-60a9-4fa6-bb86-6d7ea322a87b',
    '38f63bbe-a4c2-4e52-8e03-86721e7980e6',
  ];

  function closeSizeChartModal() {
    if (!sizeChartModal || sizeChartModal.getAttribute('hidden') !== null) return;
    sizeChartModal.classList.remove('is-open');
    document.documentElement.classList.remove('air-compare-modal-open');
    document.body.classList.remove('air-compare-modal-open');
    var overlay = sizeChartModal;
    window.setTimeout(function () {
      overlay.setAttribute('hidden', '');
    }, 220);
    if (sizeChartLastFocus && typeof sizeChartLastFocus.focus === 'function') {
      try {
        sizeChartLastFocus.focus();
      } catch (e) {}
    }
  }

  function openSizeChartModal() {
    if (!sizeChartModal) return;
    sizeChartLastFocus = document.activeElement;
    sizeChartModal.removeAttribute('hidden');
    void sizeChartModal.offsetWidth;
    sizeChartModal.classList.add('is-open');
    document.documentElement.classList.add('air-compare-modal-open');
    document.body.classList.add('air-compare-modal-open');
    var closeBtn = sizeChartModal.querySelector('.air-compare-modal__close');
    if (closeBtn) {
      try {
        closeBtn.focus();
      } catch (e) {}
    }
  }

  function prepareSizeChartClone(source) {
    var clone = source.cloneNode(true);
    if (clone.removeAttribute) {
      clone.removeAttribute('id');
      clone.removeAttribute('hidden');
      clone.removeAttribute('aria-hidden');
    }
    // The source root carries `.air-size-chart-source`, which is display:none so
    // the hidden template never flashes on the page. The clone must NOT keep it,
    // otherwise the modal content stays invisible. Re-tag it as the visible clone.
    if (clone.classList) {
      clone.classList.remove('air-size-chart-source');
      clone.classList.add('air-size-chart-clone');
    }
    clone.querySelectorAll('[id]').forEach(function (n) {
      n.removeAttribute('id');
    });
    clone.querySelectorAll('.air-reveal-pending').forEach(function (n) {
      n.classList.remove('air-reveal-pending');
      n.classList.add('air-reveal-visible');
    });
    return clone;
  }

  // CM <-> INCH tab toggle inside the size-chart modal. Locale-independent:
  // targets stable data-air-size-unit / data-air-size-table hooks, not labels.
  function initSizeUnitToggle(root) {
    var tabs = Array.prototype.slice.call(
      root.querySelectorAll('[data-air-size-unit]')
    );
    var tables = Array.prototype.slice.call(
      root.querySelectorAll('[data-air-size-table]')
    );
    if (!tabs.length || !tables.length) return;
    function activate(unit) {
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-air-size-unit') === unit;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      tables.forEach(function (table) {
        var on = table.getAttribute('data-air-size-table') === unit;
        if (on) table.removeAttribute('hidden');
        else table.setAttribute('hidden', '');
      });
    }
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        if (e) e.preventDefault();
        activate(tab.getAttribute('data-air-size-unit'));
      });
    });
  }

  function findSizeChartTriggers() {
    var seen = [];
    var triggers = [];
    SIZE_CHART_TRIGGER_RIDS.forEach(function (rid) {
      document.querySelectorAll('[data-rid="' + rid + '"]').forEach(function (el) {
        var trigger =
          el.getAttribute('role') === 'button'
            ? el
            : el.closest('[role="button"]') || el;
        if (seen.indexOf(trigger) === -1) {
          seen.push(trigger);
          triggers.push(trigger);
        }
      });
    });
    return triggers;
  }

  function buildSizeChartModal(source) {
    var overlay = document.createElement('div');
    overlay.className = 'air-compare-modal air-size-chart-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Size chart');
    overlay.setAttribute('hidden', '');

    var dialog = document.createElement('div');
    dialog.className = 'air-compare-modal__dialog air-compare-modal__dialog--table';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'air-compare-modal__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    var content = document.createElement('div');
    content.className = 'air-compare-modal__content air-compare-modal__content--table';

    var wrap = document.createElement('div');
    wrap.className = 'air-compare-modal__table-wrap air-size-chart-modal__wrap';
    wrap.appendChild(prepareSizeChartClone(source));

    content.appendChild(wrap);
    dialog.appendChild(closeBtn);
    dialog.appendChild(content);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    initSizeUnitToggle(wrap);

    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      closeSizeChartModal();
    });
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeSizeChartModal();
    });

    return overlay;
  }

  function initSizeChart() {
    try {
      var triggers = findSizeChartTriggers();
      if (!triggers.length) return;
      var open = function (e) {
        if (e) e.preventDefault();
        if (!sizeChartModal) {
          var source = document.querySelector('[data-air-size-chart-source]');
          if (!source) return;
          sizeChartModal = buildSizeChartModal(source);
        }
        openSizeChartModal();
      };
      triggers.forEach(function (trigger) {
        if (trigger.__airSizeChart) return;
        trigger.__airSizeChart = true;
        trigger.style.cursor = 'pointer';
        trigger.addEventListener('click', open);
        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') open(e);
        });
      });
    } catch (e) {}
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    if (compareModal && compareModal.getAttribute('hidden') === null) {
      closeCompareModal();
      return;
    }
    if (sizeChartModal && sizeChartModal.getAttribute('hidden') === null) {
      closeSizeChartModal();
    }
  });

  /* ---------------------------------------------------------------- *
   * 4. Scroll reveals + alchemy resume                               *
   * ---------------------------------------------------------------- */
  function isInView(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top < vh * 0.92 && r.bottom > 0;
  }
  function revealEl(el) {
    if (el.__airRevealed) return;
    el.__airRevealed = true;
    el.classList.remove('air-reveal-pending');
    el.classList.add('air-reveal-visible');
  }
  function tagReveal(el, variant, delayMs) {
    if (el.__airRevealTagged) return null;
    el.__airRevealTagged = true;
    el.setAttribute('data-air-reveal', variant || 'fade-up');
    if (delayMs) el.style.setProperty('--air-reveal-delay', delayMs + 'ms');
    el.classList.add('air-reveal-pending', 'air-reveal--' + (variant || 'fade-up'));
    if (reducedMotion || isInView(el)) {
      revealEl(el);
      return null;
    }
    return el;
  }
  function hasAlchemyPaused(el) {
    var cs = getComputedStyle(el);
    return (
      cs.animationName &&
      cs.animationName.indexOf('alchemy') !== -1 &&
      cs.animationPlayState === 'paused'
    );
  }
  function hasBg(el) {
    if ((el.style.backgroundImage || '').indexOf('url') !== -1) return true;
    var cs = getComputedStyle(el);
    return cs.backgroundImage && cs.backgroundImage !== 'none';
  }

  function isBuyBoxNode(el) {
    return !!el.closest(
      '[data-replo-product-container]:has(form[action*="/cart/add"], form[data-type="add-to-cart-form"])'
    );
  }

  function resumeBuyBoxMotion() {
    document
      .querySelectorAll(
        '[data-replo-product-container]:has(form[action*="/cart/add"], form[data-type="add-to-cart-form"])'
      )
      .forEach(function (box) {
        box.querySelectorAll('.air-reveal-pending').forEach(revealEl);
        box.querySelectorAll('[data-rid]').forEach(function (node) {
          if (!hasAlchemyPaused(node)) return;
          node.style.animationPlayState = 'running';
          node.classList.add('air-reveal--alchemy-override');
          revealEl(node);
        });
      });
  }

  function initMotion() {
    resumeBuyBoxMotion();
    var toObserve = [];
    var alchemyBg = [];
    function observe(el) {
      if (el && !el.__airRevealed) toObserve.push(el);
    }

    Array.prototype.forEach.call(scopes(), function (section) {
      // reveal carousels as a whole (not individual slides)
      section
        .querySelectorAll('[data-replo-component-root="carousel"]')
        .forEach(function (car) {
          if (car.closest('[data-replo-component-root="carousel"]') !== car) return;
          if (hasAlchemyPaused(car) && hasBg(car)) {
            alchemyBg.push(car);
            return;
          }
          observe(tagReveal(car, 'fade-up', 0));
        });
    });

    // alchemy-fade elements paused at opacity:0
    document.querySelectorAll('.air-compression-section [data-rid]').forEach(function (node) {
      if (node.__airRevealTagged) return;
      if (isBuyBoxNode(node)) return;
      if (node.closest('[data-replo-component-root="carousel"]')) return;
      if (!hasAlchemyPaused(node)) return;
      if (hasBg(node)) {
        alchemyBg.push(node);
        return;
      }
      node.classList.add('air-reveal--alchemy-override');
      observe(tagReveal(node, 'fade-up', 0));
    });

    function playAlchemy(el) {
      el.style.animationPlayState = 'running';
    }
    if (alchemyBg.length) {
      if (!('IntersectionObserver' in window) || reducedMotion) {
        alchemyBg.forEach(playAlchemy);
      } else {
        var aio = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              if (e.isIntersecting) {
                playAlchemy(e.target);
                aio.unobserve(e.target);
              }
            });
          },
          { rootMargin: '0px 0px -5% 0px', threshold: 0.01 }
        );
        alchemyBg.forEach(function (el) {
          if (isInView(el)) playAlchemy(el);
          else aio.observe(el);
        });
      }
    }

    if (!toObserve.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      toObserve.forEach(revealEl);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          revealEl(e.target);
          io.unobserve(e.target);
        }
      });
    }, REVEAL_OPTS);
    toObserve.forEach(function (el) {
      if (isInView(el)) revealEl(el);
      else io.observe(el);
    });
  }

  // Run each init in isolation so a throw in one feature (e.g. option-select
  // wiring) can never prevent a later feature (e.g. the compare / size-chart
  // popups) from initialising.
  function safe(fn) {
    try {
      fn();
    } catch (e) {}
  }

  ready(function () {
    safe(function () {
      Array.prototype.forEach.call(scopes(), function (s) {
        initProductNav(s);
      });
    });
    safe(function () {
      document.querySelectorAll('[data-replo-collapsible]').forEach(initCollapsible);
    });
    safe(function () {
      document.querySelectorAll('[data-replo-option-select]').forEach(initOptionSelect);
    });
    safe(function () {
      document.querySelectorAll('[data-replo-add-product-variant-to-cart]').forEach(initAddToCart);
    });
    safe(function () {
      document.querySelectorAll('.air-compression-section [data-replo-carousel]').forEach(initCarousel);
    });
    safe(initCompare);
    safe(initSizeChart);
    safe(initMotion);
  });

  // Re-init in the Theme Editor when a section re-renders
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target;
    if (!t) return;
    safe(function () {
      initProductNav(t);
    });
    safe(function () {
      t.querySelectorAll('[data-replo-collapsible]').forEach(initCollapsible);
    });
    safe(function () {
      t.querySelectorAll('[data-replo-option-select]').forEach(initOptionSelect);
    });
    safe(function () {
      t.querySelectorAll('[data-replo-add-product-variant-to-cart]').forEach(initAddToCart);
    });
    safe(function () {
      t.querySelectorAll('[data-replo-carousel]').forEach(initCarousel);
    });
    safe(initCompare);
    safe(initSizeChart);
    safe(initMotion);
  });
})();
